import AppSaveBar from '@/components/AppSaveBar/AppSaveBar';
import DiscardChangeModal from '@/components/DiscardChangeModal';
import Layout from '@/components/layout';
import SekeletonDashboardLayout from '@/components/SkeletonLayout/SekeletonDashboardLayout';
import { config } from '@/config';
import { SETTING_TYPE } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { handleScrollToElement, openErrorToast, openToast } from '@/helpers';
import { checkDirtyField } from '@/helpers/compare';
import { useGetWidgetAppearanceTextDetailQuery, useUpdateAppearanceTextMutation } from '@/redux/query';
import {
  handleResetWidgetTextDetail,
  handleResetWidgetTextState,
  handleSaveWidgetTextDetail,
  handleSaveWidgetTextDetailBackup,
  handleSetWidgetTextState,
  widgetTextDetailBackupSelector,
  widgetTextDetailSelector,
} from '@/redux/slice/widgetText.slice';
import { ITexts } from '@/types/components/translation';
import { BlockStack } from '@shopify/polaris';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import CartPage from './component/CartPage';
import GeneralInfo from './component/GeneralInfo';

const TranslationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const widgetTextDetail = useSelector(widgetTextDetailSelector);
  const widgetTextDetailBackup = useSelector(widgetTextDetailBackupSelector);

  const { data: widgetAppearanceTextDetail, isLoading: isLoadingWidgetAppearanceTextDetail } =
    useGetWidgetAppearanceTextDetailQuery({
      shop: config.shop,
      code: id as string,
      defaultLanguage: false,
    });
  const [isOpenDiscardModal, setOpenDiscardModal] = useState<boolean>(false);

  const [updateAppearanceText, { isLoading: isLoadingUpdateAppearanceText }] = useUpdateAppearanceTextMutation();

  useEffect(() => {
    setOpenDiscardModal(false);
    if (widgetAppearanceTextDetail?.data) {
      dispatch(handleSaveWidgetTextDetail(widgetAppearanceTextDetail?.data));
      dispatch(handleSaveWidgetTextDetailBackup(widgetAppearanceTextDetail?.data));
    }
  }, [dispatch, widgetAppearanceTextDetail?.data]);

  const isChangeData = useMemo(() => {
    return checkDirtyField(widgetTextDetail, widgetTextDetailBackup).isDirty;
  }, [widgetTextDetail, widgetTextDetailBackup]);

  const discardChange = useCallback(() => {
    dispatch(handleResetWidgetTextDetail());
    dispatch(handleResetWidgetTextState());
  }, [dispatch]);

  const widgetPickupTextData = useMemo(() => {
    return widgetTextDetail?.texts?.find((item: ITexts) => item.type === SETTING_TYPE.PICKUP);
  }, [widgetTextDetail.texts]);

  const widgetDeliveryTextData = useMemo(() => {
    return widgetTextDetail?.texts?.find((item: ITexts) => item.type === SETTING_TYPE.DELIVERY);
  }, [widgetTextDetail.texts]);

  const widgetShippingTextData = useMemo(() => {
    return widgetTextDetail?.texts?.find((item: ITexts) => item.type === SETTING_TYPE.SHIPPING);
  }, [widgetTextDetail.texts]);

  const handleSubmitWidgetTextDetail = () => {
    dispatch(handleSetWidgetTextState({ isSaving: true }));

    if (isEmpty(widgetTextDetail.name?.trim())) {
      handleScrollToElement('text-detail-general');
      return;
    }

    updateAppearanceText({
      shop: config.shop,
      code: widgetTextDetail?.code || '',
      defaultLanguage: false,
      name: widgetTextDetail?.name?.trim() || '',
      headerTitle: widgetTextDetail?.headerTitle?.trim() || '',
      additionalMessage: widgetTextDetail?.additionalMessage?.trim() || '',
      texts: [
        {
          type: SETTING_TYPE.PICKUP,
          labelAndButtons: {
            buttonLabel: widgetPickupTextData?.labelAndButtons?.buttonLabel || '',
            instructions: widgetPickupTextData?.labelAndButtons?.instructions || '',
            dateTitle: widgetPickupTextData?.labelAndButtons?.dateTitle || '',
            timeTitle: widgetPickupTextData?.labelAndButtons?.timeTitle || '',
            zipCodeLabel: widgetPickupTextData?.labelAndButtons?.zipCodeLabel || '',
            checkButton: widgetPickupTextData?.labelAndButtons?.checkButton || '',
            captionDateTitle: widgetPickupTextData?.labelAndButtons?.captionDateTitle || '',
            captionTimeTitle: widgetPickupTextData?.labelAndButtons?.captionTimeTitle || '',
          },
          isUseAdditionalNote: widgetPickupTextData?.isUseAdditionalNote,
          isShowMessageMinimum: widgetPickupTextData?.isShowMessageMinimum,
          requiredConditions: {
            beforeAddToCart: widgetPickupTextData?.requiredConditions?.beforeAddToCart || '',
            beforeCheckout: widgetPickupTextData?.requiredConditions?.beforeCheckout || '',
            eligibleNotice: widgetPickupTextData?.requiredConditions?.eligibleNotice || '',
          },
          errorMessages: {
            eligibleMessage: widgetPickupTextData?.errorMessages?.eligibleMessage || '',
            notSupportedZone: widgetPickupTextData?.errorMessages?.notSupportedZone || '',
          },
          additionalMessages: {
            minimumOrderValue: widgetPickupTextData?.additionalMessages?.minimumOrderValue || '',
            minimumOrderWeight: widgetPickupTextData?.additionalMessages?.minimumOrderWeight || '',
          },
          orderStatusMessage: {
            heading: widgetPickupTextData?.orderStatusMessage?.heading || '',
            location: widgetPickupTextData?.orderStatusMessage?.location || '',
            selectedDate: widgetPickupTextData?.orderStatusMessage?.selectedDate || '',
            selectedTime: widgetPickupTextData?.orderStatusMessage?.selectedTime || '',
          },
        },
        {
          type: SETTING_TYPE.DELIVERY,
          labelAndButtons: {
            buttonLabel: widgetDeliveryTextData?.labelAndButtons?.buttonLabel || '',
            instructions: widgetDeliveryTextData?.labelAndButtons?.instructions || '',
            dateTitle: widgetDeliveryTextData?.labelAndButtons?.dateTitle || '',
            timeTitle: widgetDeliveryTextData?.labelAndButtons?.timeTitle || '',
            zipCodeLabel: widgetDeliveryTextData?.labelAndButtons?.zipCodeLabel || '',
            checkButton: widgetDeliveryTextData?.labelAndButtons?.checkButton || '',
            captionDateTitle: widgetDeliveryTextData?.labelAndButtons?.captionDateTitle || '',
            captionTimeTitle: widgetDeliveryTextData?.labelAndButtons?.captionTimeTitle || '',
          },
          isUseAdditionalNote: widgetDeliveryTextData?.isUseAdditionalNote,
          isShowMessageMinimum: widgetDeliveryTextData?.isShowMessageMinimum,
          requiredConditions: {
            beforeAddToCart: widgetDeliveryTextData?.requiredConditions?.beforeAddToCart || '',
            beforeCheckout: widgetDeliveryTextData?.requiredConditions?.beforeCheckout || '',
            eligibleNotice: widgetDeliveryTextData?.requiredConditions?.eligibleNotice || '',
          },
          errorMessages: {
            eligibleMessage: widgetDeliveryTextData?.errorMessages?.eligibleMessage || '',
            notSupportedZone: widgetDeliveryTextData?.errorMessages?.notSupportedZone || '',
          },
          additionalMessages: {
            minimumOrderValue: widgetDeliveryTextData?.additionalMessages?.minimumOrderValue || '',
            minimumOrderWeight: widgetDeliveryTextData?.additionalMessages?.minimumOrderWeight || '',
          },
        },
        {
          type: SETTING_TYPE.SHIPPING,
          labelAndButtons: {
            buttonLabel: widgetShippingTextData?.labelAndButtons?.buttonLabel || '',
            instructions: widgetShippingTextData?.labelAndButtons?.instructions || '',
            dateTitle: widgetShippingTextData?.labelAndButtons?.dateTitle || '',
            timeTitle: widgetShippingTextData?.labelAndButtons?.timeTitle || '',
            zipCodeLabel: widgetShippingTextData?.labelAndButtons?.zipCodeLabel || '',
            checkButton: widgetShippingTextData?.labelAndButtons?.checkButton || '',
            captionDateTitle: widgetShippingTextData?.labelAndButtons?.captionDateTitle || '',
            captionTimeTitle: widgetShippingTextData?.labelAndButtons?.captionTimeTitle || '',
          },
          isUseAdditionalNote: widgetShippingTextData?.isUseAdditionalNote,
          isShowMessageMinimum: widgetShippingTextData?.isShowMessageMinimum,
          requiredConditions: {
            beforeAddToCart: widgetShippingTextData?.requiredConditions?.beforeAddToCart || '',
            beforeCheckout: widgetShippingTextData?.requiredConditions?.beforeCheckout || '',
            eligibleNotice: widgetShippingTextData?.requiredConditions?.eligibleNotice || '',
          },
          errorMessages: {
            eligibleMessage: widgetShippingTextData?.errorMessages?.eligibleMessage || '',
            notSupportedZone: widgetShippingTextData?.errorMessages?.notSupportedZone || '',
          },
          additionalMessages: {
            minimumOrderValue: widgetShippingTextData?.additionalMessages?.minimumOrderValue || '',
            minimumOrderWeight: widgetShippingTextData?.additionalMessages?.minimumOrderWeight || '',
          },
        },
      ],
    })
      .unwrap()
      .then(() => {
        dispatch(handleResetWidgetTextState());
      })
      .then(() => openToast(dispatch, 'Saved successfully'))
      .catch((err) => openErrorToast(dispatch, err.message));
  };

  const handleDiscard = useCallback(() => {
    discardChange();
    setOpenDiscardModal(false);
  }, [discardChange]);

  const handleLeavePage = useCallback(() => {
    handleDiscard();
    setTimeout(() => {
      navigate({ pathname: PATH.TRANSLATION.pathname });
    }, 100);
  }, [handleDiscard, navigate]);

  const handleStay = () => setOpenDiscardModal(false);

  const handleBackAction = useCallback(() => {
    if (isChangeData) {
      setOpenDiscardModal(true);
    } else {
      handleLeavePage();
    }
  }, [handleLeavePage, isChangeData]);

  return (
    <Layout
      title={widgetTextDetail?.name || ''}
      backAction={{
        content: 'translation',
        onAction: handleBackAction,
      }}
    >
      <AppSaveBar
        open={isChangeData}
        loading={isLoadingUpdateAppearanceText}
        onSave={handleSubmitWidgetTextDetail}
        onDiscard={discardChange}
      />
      {isLoadingWidgetAppearanceTextDetail ? (
        <SekeletonDashboardLayout />
      ) : (
        <BlockStack>
          <GeneralInfo />
          <CartPage />
          <DiscardChangeModal
            isOpen={isOpenDiscardModal}
            onClose={() => setOpenDiscardModal(false)}
            onStay={handleStay}
            onLeave={handleLeavePage}
          />
        </BlockStack>
      )}
    </Layout>
  );
};

export default TranslationDetail;
